import { useState, useRef, useCallback, useEffect } from 'react';
import api from '../lib/api';

export interface ChatMessage {
  id: number;
  content: string;
  isAssistant: boolean;
  timestamp: Date;
}

interface UseChatOptions {
  apiEndpoint: string;
  requestPayload?: Record<string, any>;
  onError?: (error: any) => void;
  timeout?: number;
}

export function useChat({
  apiEndpoint,
  requestPayload = {},
  onError,
  timeout = 300000, // 5 minutes default timeout
}: UseChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup function to abort any pending requests and clear timeouts
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const sendMessage = useCallback(
    async (message: string) => {
      // Abort any existing request
      cleanup();

      // Create a new AbortController
      abortControllerRef.current = new AbortController();

      try {
        setIsProcessing(true);

        // Add user message
        const userMessageId = Date.now();
        setMessages(prev => [
          ...prev,
          {
            id: userMessageId,
            content: message,
            isAssistant: false,
            timestamp: new Date(),
          },
        ]);

        // Add typing indicator
        const typingIndicatorId = Date.now() + 1;
        setMessages(prev => [
          ...prev,
          {
            id: typingIndicatorId,
            content: '...',
            isAssistant: true,
            timestamp: new Date(),
          },
        ]);

        // Set timeout to abort request if it takes too long
        if (timeout > 0) {
          timeoutRef.current = setTimeout(() => {
            if (abortControllerRef.current) {
              abortControllerRef.current.abort('timeout');
            }
          }, timeout);
        }

        // Send request to API
        const response = await api.post(
          apiEndpoint,
          { ...requestPayload, message },
          {
            signal: abortControllerRef.current.signal,
            timeout: timeout, // Set axios timeout to match our timeout
          }
        );

        // Clear timeout since request completed successfully
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        // Remove typing indicator and add response
        setMessages(prev => {
          const withoutTyping = prev.filter(msg => msg.id !== typingIndicatorId);

          if (response.data && response.data.received) {
            return [
              ...withoutTyping,
              {
                id: Date.now(),
                content: response.data.message,
                isAssistant: true,
                timestamp: new Date(),
              },
            ];
          }

          return withoutTyping;
        });
      } catch (error: any) {
        // Clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        // Handle timeout specifically
        const isTimeout = error.name === 'AbortError' && error.message === 'timeout';
        const isAxiosTimeout = error.code === 'ECONNABORTED';

        // Don't show error for manually aborted requests
        if (
          error.name === 'AbortError' &&
          error.message !== 'timeout' &&
          !error.message.includes('canceled')
        ) {
          return;
        }

        // Remove typing indicator and add error message
        setMessages(prev => {
          const withoutTyping = prev.filter(msg => msg.content !== '...');

          let errorMessage = 'Erro ao processar mensagem. Por favor, tente novamente.';

          if (isTimeout || isAxiosTimeout) {
            errorMessage =
              'O tempo limite para resposta foi excedido. O servidor pode estar ocupado, por favor tente novamente mais tarde.';
          } else if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.message) {
            errorMessage = `Erro: ${error.message}`;
          }

          return [
            ...withoutTyping,
            {
              id: Date.now(),
              content: errorMessage,
              isAssistant: true,
              timestamp: new Date(),
            },
          ];
        });

        if (onError) {
          onError(error);
        }
      } finally {
        setIsProcessing(false);
        abortControllerRef.current = null;
      }
    },
    [apiEndpoint, requestPayload, cleanup, timeout, onError]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    isProcessing,
    sendMessage,
    clearMessages,
    abortRequest: cleanup,
  };
}
